import * as React from 'react'

import { TFPaymentMethod } from '~graphql/generated/clientGraphql'
import { isDefined } from '~logic/helpers/isDefined'
import { MultiSwitch } from '~ui/components/formElements/MultiSwitch/MultiSwitch'
import { Box } from '~ui/components/layout/Box'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { Text } from '~ui/components/typography/Text/Text'

type TProps = NoChildren & {
  isDisabled?: boolean
  selectedMethod: Nullable<TFPaymentMethod>
  availableMethods: Nullable<RoA<TFPaymentMethod>>
  onSelect: (selectedMethod: TFPaymentMethod) => void
}

/**
 * Multi switch with all of the available payment methods.
 * It also preselects the first method once the options are loaded.
 */
export const PaymentMethodSelect: React.FC<TProps> = ({
  isDisabled = false,
  selectedMethod,
  availableMethods,
  onSelect,
}) => {
  // filter out wallet credit, as it is handled separately (via credit slider)
  const filteredMethods = availableMethods?.filter(
    (method) => method.paymentGate.enum !== `WALLET_CREDIT`,
  )
  // auto-select the first available method once those are loaded
  // if no method was selected yet OR the selected method is not available anymore
  React.useEffect(() => {
    if (!filteredMethods || filteredMethods.length === 0) return

    const isSelectedMethod = getIsSelectedMethod(selectedMethod)
    const isSelectedMethodStillAvailable = filteredMethods.find(isSelectedMethod)

    if (isSelectedMethodStillAvailable) return

    // recent changes in react-hook-form,
    // probably this one: https://github.com/react-hook-form/react-hook-form/blob/master/CHANGELOG.md#changed-6
    // cause that the newly selected method from this useEffect is not reflected by the Controller that is wrapping the instance of `PaymentMethodSelect` of the `FormScreen`
    // to fix it, we call onSelect inside of setTimeout to be called after react-hook-form internal code
    const timeout = setTimeout(() => {
      onSelect(filteredMethods[0])
    }, 0)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [filteredMethods, onSelect, selectedMethod])

  // methods loading -> show skeleton (error is handled somewhere else)
  if (!filteredMethods || !selectedMethod) {
    return (
      <Skeleton>
        <Box height={`3.25rem`}></Box>
      </Skeleton>
    )
  }

  // TODO cash for 0 value orders? (also disable tip & set to 0)

  return (
    <MultiSwitch
      idProp={`id`}
      variant={`buttons`}
      onSelect={onSelect}
      isDisabled={isDisabled}
      options={filteredMethods}
      selectedOption={selectedMethod}
      renderLabel={(option, isSelected) => (
        <Text
          fontStyle={`boldSemi`}
          textTransform={`lowercase`}
          contrast={isSelected ? `high` : `normal`}
        >
          {option.label}
        </Text>
      )}
    />
  )
}

// * HELPERS

const getIsSelectedMethod =
  (selectedMethod: Nullable<TFPaymentMethod>) => (method: TFPaymentMethod) => {
    return isDefined(selectedMethod) && method.id === selectedMethod?.id
  }
